import { EVENTS } from '../analytics/events';
import { track } from '../analytics/track';

declare const chrome: {
  runtime?: {
    lastError?: { message: string };
    sendMessage: (
      extensionId: string,
      message: unknown,
      callback: (response: Record<string, unknown> | undefined) => void,
    ) => void;
  };
};

const CHROME_EXTENSION_STORE_ID = 'kdifgpmgfbififkcidjnjehepfhlbbbk';

export const CHROME_EXTENSION_URL = `https://chromewebstore.google.com/detail/academia-co-scientist/${CHROME_EXTENSION_STORE_ID}`;

function getExtensionId(): string {
  return (
    // dev (unpacked) extension has a different ID than the Web Store listing
    document.documentElement.dataset.academiaCoScientistExtensionId ??
    CHROME_EXTENSION_STORE_ID
  );
}

export function isChromiumBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Chrome\//.test(ua) || /Edg\//.test(ua) || /OPR\//.test(ua);
}

export function isFirefoxBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Firefox\//.test(navigator.userAgent);
}

export function browserExtensionName(): string {
  if (isFirefoxBrowser()) return 'Firefox extension';
  if (isChromiumBrowser()) return 'Chrome extension';
  return 'Browser extension'; // capitalized: callers use this to start a sentence
}

// Uses the extension's externally_connectable messaging API
export function sendExtensionMessage(
  message: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  return new Promise((resolve) => {
    if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
      resolve(null);
      return;
    }
    const runtime = chrome.runtime;
    try {
      runtime.sendMessage(getExtensionId(), message, (response) => {
        if (runtime.lastError) {
          resolve(null);
          return;
        }
        resolve(response ?? null);
      });
    } catch {
      resolve(null);
    }
  });
}

export function notifyExtensionUnreadCount(count: number): void {
  // Extension also polls independently; this just makes the badge update sooner
  void sendExtensionMessage({ type: 'unread_count', count });
}

const INSTALL_COOKIE = 'co_scientist_extension_install';

export function openExtensionInstallPage(): void {
  track(EVENTS.EXTENSION_INSTALL_CLICK, {});
  const returnUrl = window.location.href;
  // Extension reads this cookie after install to confirm the referring page
  document.cookie = `${INSTALL_COOKIE}=${encodeURIComponent(returnUrl)}; path=/; max-age=600; SameSite=Lax`;
  window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer');
}

export function requestExtensionUninstall(): Promise<boolean> {
  return sendExtensionMessage({ type: 'uninstall' }).then(
    (r) => r != null && (r as { ok?: boolean }).ok === true,
  );
}

export function requestExtensionPinState(): Promise<boolean | null> {
  // only the extension can read pin state (getUserSettings is background-only)
  return sendExtensionMessage({ type: 'pin_state' }).then((r) => {
    const value = (r as { isOnToolbar?: unknown } | null)?.isOnToolbar;
    return typeof value === 'boolean' ? value : null;
  });
}
